import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, apiError, uploadFile } from "@/lib/api";
import { Avatar } from "@/components/shared/Avatar";
import { AppVersionLine } from "@/components/shared/UpdateAvailable";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { setActiveCurrency } from "@/lib/format";
import { formatDateTime } from "@/lib/format";
import { McpConnector } from "@/components/settings/McpConnector";
import { PermissionsMatrix } from "@/components/shared/PermissionsMatrix";
import { SubscriptionPanel } from "@/components/settings/SubscriptionPanel";
import { WorkflowUkoly } from "@/components/settings/WorkflowUkoly";
import { PushRows } from "@/components/settings/PushRow";
import { PageHeader, useIsMobile } from "@/components/shared/PagePrimitives";
import { Group, Row, Segmented, rowInput } from "@/components/settings/Grouped";
import { ResetDataDialog } from "@/components/settings/ResetDataDialog";
import { EmailChangeDialog } from "@/components/settings/EmailChange";
import { SignedImg } from "@/components/shared/SignedImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Sun, Moon, Plus, Minus, X, ShieldCheck, User, Building2, Clock4, AlertTriangle, Camera, Pencil, Check, Users, ArrowUpDown, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, GripVertical, ReceiptText, ListChecks, CreditCard, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";
import { GuideRow } from "@/components/GuideNudge";
import { glassMode, glassOn, setGlassMode, GLASS_EVENT } from "@/lib/glass";
import { useSlidingPill } from "@/components/shared/Pill";
import { storage } from "@/lib/storage";
import { defaultAmountMode } from "@/lib/documents";
import { AM_HINT } from "@/components/shared/AmountPick";
import { NumInput } from "@/components/shared/NumInput";

const AUDIT_LABELS = {
  login: "Přihlášení",
  login_failed: "Neúspěšné přihlášení",
  logout: "Odhlášení",
  register: "Registrace firmy",
  password_reset: "Změna hesla",
  team_invite: "Pozvánka do týmu",
  accept_invite: "Přijetí pozvánky",
  project_delete: "Smazání zakázky",
  document_delete: "Smazání dokladu",
  receipt_delete: "Smazání účtenky",
  role_change: "Změna role",
  attendance_export: "Export docházky",
  economics_export: "Export ekonomiky",
};

const CURRENCIES = [
  { code: "CZK", label: "Koruna", symbol: "Kč" },
  { code: "EUR", label: "Euro", symbol: "€" },
];

// Sekční karta se stejným vzhledem napříč stránkou (231: jako v ProfitPilotu — nadpis + popis, bez ikony).
function Section({ icon: Icon, title, desc, children, testid, className }) {
  return (
    <section className={`bg-card border border-border rounded-[20px] p-5 md:p-6 min-w-0 ${className || ""}`} data-testid={testid}>
      <h2 className="text-[17px] font-bold">{title}</h2>
      {desc && <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

// Editor seznamu štítků (kategorie nákladů / výdajů) — sdílený pro obě karty.
// Tolerantní klíč kategorie (165) — musí odpovídat services.canon_category.
const canonCat = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

function TagEditor({ items, kind, onAdd, onRemove, onRenamed, onReorder, placeholder, testid }) {
  const [val, setVal] = useState("");
  // 232: pořadí kategorií (tak se nabízejí u dokladů a účtenek). Šipky fungují
  // všude, na počítači jde řádek i přetáhnout. Uloží se až tlačítkem Hotovo.
  const [order, setOrder] = useState(null);
  const [drag, setDrag] = useState(null);
  const move = (from, to) => setOrder((o) => {
    if (to < 0 || to >= o.length || from === to) return o;
    const n = [...o]; const [x] = n.splice(from, 1); n.splice(to, 0, x); return n;
  });
  const saveOrder = async () => {
    setBusy(true);
    try { if (order.join("\u0000") !== items.join("\u0000")) await onReorder(order); setOrder(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [merge, setMerge] = useState(null);   // {from, to, usage}
  const add = async () => {
    const t = val.trim();
    if (!t || items.some((x) => canonCat(x) === canonCat(t))) return;
    try { await onAdd(t); setVal(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (c) => { setEditing(c); setEditVal(c); setMerge(null); };
  const cancelEdit = () => { setEditing(null); setEditVal(""); };
  const doRename = async (from, to, confirmMerge = false) => {
    const t = (to || "").trim();
    if (!t) return;
    if (canonCat(t) === canonCat(from)) { cancelEdit(); return; }
    const exists = items.some((x) => x !== from && canonCat(x) === canonCat(t));
    if (exists && !confirmMerge) {
      try {
        const { data } = await api.get("/company/categories/usage", { params: { kind, name: from } });
        setMerge({ from, to: t, usage: data });
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/company/categories/rename", { kind, from, to: t });
      toast.success(`Přejmenováno · ${data.total} záznamů`);
      onRenamed && onRenamed(data.categories);
      cancelEdit(); setMerge(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    try { await onRemove(c); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const mergeCount = merge ? (kind === "cost"
    ? `${merge.usage.documents} dokladů a ${merge.usage.line_items} řádků`
    : `${merge.usage.receipts} účtenek`) : "";
  if (order) {
    return (
      <div data-testid={`${testid}-order`}>
        <div className="rounded-[14px] border border-border divide-y divide-border overflow-hidden w-full md:max-w-md mb-3">
          {order.map((c, i) => (
            <div key={c} draggable onDragStart={() => setDrag(i)} onDragEnd={() => setDrag(null)}
              onDragOver={(e) => { e.preventDefault(); if (drag !== null && drag !== i) { move(drag, i); setDrag(i); } }}
              className={`flex items-center gap-2 pl-2 pr-1 h-12 bg-card ${drag === i ? "opacity-50" : ""}`} data-testid={`${testid}-order-${i}`}>
              <GripVertical size={16} className="hidden md:block text-muted-foreground shrink-0 cursor-grab" aria-hidden />
              <span className="w-6 text-center text-xs text-muted-foreground tabular-nums shrink-0">{i + 1}</span>
              <span className="flex-1 min-w-0 truncate text-sm font-semibold">{c}</span>
              <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label={`Posunout ${c} výš`}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
                data-testid={`${testid}-up-${i}`}><ChevronUp size={18} /></button>
              <button type="button" onClick={() => move(i, i + 1)} disabled={i === order.length - 1} aria-label={`Posunout ${c} níž`}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
                data-testid={`${testid}-down-${i}`}><ChevronDown size={18} /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={() => setOrder(null)} disabled={busy} className="h-10 w-full sm:w-auto font-bold">Zrušit</Button>
          <Button onClick={saveOrder} disabled={busy} className="h-10 w-full sm:w-auto font-bold px-6" data-testid={`${testid}-order-save`}>{busy ? "Ukládám…" : "Hotovo"}</Button>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((c) => (editing === c ? (
          <span key={c} className="flex items-center gap-1.5 w-full sm:w-auto">
            <Input autoFocus value={editVal} disabled={busy} maxLength={40}
              onChange={(e) => setEditVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doRename(c, editVal); if (e.key === "Escape") cancelEdit(); }}
              className="h-9 w-full sm:w-48" data-testid={`${testid}-edit-input`} />
            <Button size="sm" onClick={() => doRename(c, editVal)} disabled={busy} data-testid={`${testid}-edit-save`} className="h-9 font-bold px-2"><Check size={15} /></Button>
            <button onClick={cancelEdit} className="text-muted-foreground hover:text-foreground p-1"><X size={15} /></button>
          </span>
        ) : (
          <span key={c} className="flex items-center gap-1.5 bg-secondary px-3 py-1.5 rounded-md text-sm font-semibold" data-testid={`${testid}-tag-${c}`}>
            {c}
            <button onClick={() => startEdit(c)} data-testid={`${testid}-rename-${c}`} title="Přejmenovat" className="text-muted-foreground hover:text-brand"><Pencil size={12} /></button>
            <button onClick={() => remove(c)} data-testid={`${testid}-remove-${c}`} className="text-muted-foreground hover:text-destructive"><X size={13} /></button>
          </span>
        )))}
        {items.length === 0 && <span className="text-sm text-muted-foreground">Zatím žádné.</span>}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} maxLength={40}
          placeholder={placeholder} className="h-10 w-full sm:max-w-xs" data-testid={`${testid}-input`} />
        <Button onClick={add} data-testid={`${testid}-add`} className="h-10 w-full sm:w-auto font-bold"><Plus size={16} /> Přidat</Button>
        {onReorder && items.length > 1 && (
          <Button variant="ghost" onClick={() => { cancelEdit(); setOrder([...items]); }} data-testid={`${testid}-reorder`}
            className="h-10 w-full sm:w-auto sm:ml-auto font-bold text-muted-foreground hover:text-foreground"><ArrowUpDown size={15} /> Změnit pořadí</Button>
        )}
      </div>
      <AlertDialog open={!!merge} onOpenChange={(o) => !o && setMerge(null)}>
        <AlertDialogContent data-testid={`${testid}-merge-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Sloučit kategorie?</AlertDialogTitle>
            <AlertDialogDescription>
              {merge && <>Sloučit „{merge.from}" do „{merge.to}"? Přepíše se u {mergeCount}.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-merge-cancel`}>Zrušit</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => merge && doRename(merge.from, merge.to, true)} data-testid={`${testid}-merge-confirm`}>Sloučit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// 259: sekce Nastavení — na počítači vodorovné záložky, na telefonu seznam (jako Nastavení
// v iPhonu) a klepnutím detail. Obsah sekcí je seskupený do řádků s hodnotou vpravo.
const SECTION_META = {
  profil: { label: "Můj profil", icon: User },
  firma: { label: "Firma", icon: Building2 },
  dochazka: { label: "Docházka", icon: Clock4 },
  tym: { label: "Tým a role", icon: Users },
  doklady: { label: "Doklady a účtenky", short: "Doklady", icon: ReceiptText },
  ukoly: { label: "Automatické úkoly", short: "Úkoly", icon: ListChecks },
  predplatne: { label: "Předplatné", icon: CreditCard },
  pristupy: { label: "AI a zabezpečení", icon: ShieldCheck },
};
// Staré klíče v URL (e-maily, záložky, průvodce) nesmí spadnout na první záložku.
const ALIAS = { prace: "dochazka", kategorie: "doklady", workflow: "ukoly", opravneni: "tym",
                propojeni: "pristupy", bezpecnost: "pristupy" };
const MODE_LABELS = { site: "Na stavbě", daysheet: "Denní výkaz" };
const halfHours = (from, to, cur) => {
  const out = [];
  for (let m = from; m <= to; m += 30) out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${m % 60 ? "30" : "00"}`);
  if (cur && !out.includes(cur)) out.push(cur);
  return out.sort();
};
const hoursText = (h) => `${String(h).replace(".", ",")} h`;

function TimeSelect({ value, onChange, from, to, testid, disabled }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-10 w-[104px] shrink-0 tabular-nums" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{halfHours(from, to, value).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
    </Select>
  );
}

// 403: příplatky ke mzdě — [klíč, popisek, zákonné minimum %]
const PREMIUM_ROWS = [["overtime", "Přesčas", 25], ["weekend", "Sobota a neděle", 10], ["night", "Noc 22–6", 10], ["holiday", "Práce ve svátek", 100]];

// Procento s uložením při odchodu z pole (0–300 %); neplatné vrátí původní hodnotu.
function PercentField({ value, onSave, disabled, testid }) {
  const [v, setV] = useState(String(value ?? ""));
  useEffect(() => { setV(String(value ?? "")); }, [value]);
  const commit = () => {
    const n = Number(String(v).replace(",", "."));
    if (v === "" || !Number.isFinite(n) || n < 0 || n > 300) { setV(String(value ?? "")); if (v !== String(value ?? "")) toast.error("Příplatek je 0–300 %."); return; }
    if (n !== value) onSave(n);
  };
  return (
    <span className="inline-flex items-center gap-1.5 shrink-0">
      <NumInput value={v} onChange={setV} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} disabled={disabled}
        min={0} max={300} step={1} data-testid={testid} aria-label="Příplatek v procentech" className="h-10 w-[84px] text-right tabular-nums" />
      <span className="text-muted-foreground font-bold">%</span>
    </span>
  );
}

const MONEY_ROLES = ["OWNER", "MANAGER", "ASSISTANT", "ACCOUNTANT"];   // 394: vidí částky v Dokladech nebo Objednávkách

export default function Settings() {
  const { user, company, refresh } = useAuth();
  const [emailOpen, setEmailOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const mobile = useIsMobile();
  // 382: skleněné plochy — auto / zapnuto / vypnuto; „auto“ hlásí, když sklo samo vypnulo
  const [glass, setGlass] = useState(() => ({ mode: glassMode(), on: glassOn() }));
  useEffect(() => {
    const h = () => setGlass({ mode: glassMode(), on: glassOn() });
    window.addEventListener(GLASS_EVENT, h);
    return () => window.removeEventListener(GLASS_EVENT, h);
  }, []);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", photo_url: user?.photo_url || null });
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInput = useRef(null);
  const isOwner = user?.role === "OWNER";
  // Konektor: musí odpovídat MCP_ROLES v backend/routes/mcp.py (vč. asistentky).
  const canMcp = ["OWNER", "MANAGER", "ASSISTANT"].includes(user?.role);
  // Administrativa firmy (údaje, měna, docházka, číselníky): majitel a asistent/ka.
  // Stavbyvedoucí ji nevidí — řídí stavby, ne mzdy. Backend to už takhle povoluje.
  const admin = isOwner || user?.role === "ASSISTANT";
  const [cats, setCats] = useState(company?.cost_categories || ["Materiál", "Subdodávka", "Doprava", "Nářadí", "Ostatní"]);
  const [rCats, setRCats] = useState(company?.receipt_categories || ["PHM (pohonné hmoty)", "Kancelářské vybavení", "Ubytování (služební cesty)", "Cestovné a diety", "Stravné a reprezentace"]);
  const [audit, setAudit] = useState([]);
  const [curBusy, setCurBusy] = useState(false);
  const currency = company?.currency || "CZK";
  const [vatPayer, setVatPayer] = useState(company?.vat_payer !== false);
  const [vatBusy, setVatBusy] = useState(false);
  // Odvody zaměstnavatele (280): hodinová sazba je hrubá mzda, firma platí navíc pojištění.
  const pctText = (v) => String(v ?? 34.78).replace(".", ",");
  const [laborPct, setLaborPct] = useState(pctText(company?.labor_overhead_pct));
  const [laborBusy, setLaborBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);     // 285: Smazat data firmy
  const [autoNewProj, setAutoNewProj] = useState(!!company?.auto_team_new_projects);
  const [autoNewMem, setAutoNewMem] = useState(!!company?.auto_team_new_members);
  const [teamFlagBusy, setTeamFlagBusy] = useState(false);
  const [att, setAtt] = useState(null);
  const [attBusy, setAttBusy] = useState(false);
  const companyForm = (c) => ({
    name: c?.name || "", ico: c?.ico || "", dic: c?.dic || "", street: c?.street || "", zip: c?.zip || "",
    city: c?.city || "", web: c?.web || "", logo_url: c?.logo_url || "",
  });
  const [cForm, setCForm] = useState(() => companyForm(company));
  const [cSaved, setCSaved] = useState(() => companyForm(company));
  const [cBusy, setCBusy] = useState(false);
  const [ares, setAres] = useState(null);          // null | busy | done | nf
  const [aresVat, setAresVat] = useState(false);   // DPH nastavené podle registru

  useEffect(() => {
    if (isOwner) api.get("/audit-log").then((r) => setAudit(r.data)).catch(() => {});
    if (admin) api.get("/company/attendance-settings").then((r) => setAtt(r.data)).catch(() => {});
  }, [isOwner, admin]);

  // Osobní režim docházky přebíjí firemní výchozí — proto se změna firemního
  // nastavení u takového uživatele nikde neprojeví. Musí to být vidět.
  const myOverride = user?.attendance_mode === "site" || user?.attendance_mode === "daysheet" ? user.attendance_mode : null;
  const clearMyOverride = async () => {
    setAttBusy(true);
    try {
      await api.patch(`/team/${user.id}`, { attendance_mode: "inherit" });
      toast.success("Váš osobní režim zrušen — platí firemní nastavení.");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAttBusy(false); }
  };
  // Změna režimu je zásadní (přepíná způsob evidence celé firmě) — potvrdit.
  const changeMode = async (v, label) => {
    if (att?.attendance_mode_default === v) return;
    if (!await ask(`Přepnout výchozí režim docházky firmy na „${label}"?\n\nZmění se způsob evidence práce pro všechny pracovníky, kteří nemají vlastní režim nastavený v kartě.`)) return;
    saveAtt({ attendance_mode_default: v });
  };
  const saveAtt = async (patch) => {
    setAtt((a) => ({ ...a, ...patch }));
    setAttBusy(true);
    try { const { data } = await api.patch("/company/attendance-settings", patch); setAtt(data); toast.success("Nastavení docházky uloženo."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAttBusy(false); }
  };
  const saveCats = async (list) => {
    const prev = cats; setCats(list);
    try { await api.patch("/company", { cost_categories: list }); refresh(); }
    catch (e) { setCats(prev); throw e; }
  };
  const saveRCats = async (list) => {
    const prev = rCats; setRCats(list);
    try { await api.patch("/company", { receipt_categories: list }); refresh(); }
    catch (e) { setRCats(prev); throw e; }
  };
  const saveVatPayer = async (val, quiet) => {
    setVatPayer(val); setVatBusy(true);
    try { await api.patch("/company", { vat_payer: val }); if (!quiet) toast.success("Nastavení DPH uloženo."); refresh(); }
    catch (e) { setVatPayer(!val); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setVatBusy(false); }
  };
  const saveLaborPct = async () => {
    const prev = company?.labor_overhead_pct ?? 34.78;
    const n = Number(laborPct.replace(",", ".").replace("%", "").trim());
    if (laborPct.trim() === "" || !Number.isFinite(n) || n < 0 || n > 100) {
      toast.error("Zadej procento od 0 do 100."); setLaborPct(pctText(prev)); return;
    }
    if (n === prev) { setLaborPct(pctText(prev)); return; }
    setLaborBusy(true);
    try { await api.patch("/company", { labor_overhead_pct: n }); toast.success("Odvody uloženy. Ekonomika zakázek je už počítá."); refresh(); }
    catch (e) { setLaborPct(pctText(prev)); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLaborBusy(false); }
  };
  const saveTeamFlag = async (key, val) => {
    const setter = key === "auto_team_new_projects" ? setAutoNewProj : setAutoNewMem;
    setter(val); setTeamFlagBusy(true);
    try { await api.patch("/company", { [key]: val }); toast.success("Nastavení týmu uloženo."); refresh(); }
    catch (e) { setter(!val); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTeamFlagBusy(false); }
  };
  const saveProfile = async () => {
    try { await api.patch(`/team/${user.id}`, form); toast.success("Profil uložen."); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // Fotka se ukládá hned po vybrání — avatar se objeví všude okamžitě.
  const savePhoto = async (photo_url) => {
    setPhotoBusy(true);
    try {
      await api.patch(`/team/${user.id}`, { photo_url });
      setForm((f) => ({ ...f, photo_url }));
      toast.success(photo_url ? "Fotka uložena." : "Fotka odebrána.");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPhotoBusy(false); }
  };
  // 394: výchozí částky u účtu; přepnutí v Zobrazení na tomhle zařízení se zahodí, ať je nová volba hned vidět
  const saveAmounts = async (mode) => {
    try {
      await api.put("/auth/amounts", { mode });
      storage.remove("orders_amounts");
      const v = String(storage.get("docs_view") || "").split("|");
      if (v.length > 4) storage.set("docs_view", v.slice(0, 4).join("|"));
      await refresh();
      toast.success(`Částky v Dokladech a Objednávkách: ${AM_HINT[mode]}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoBusy(true);
    let url;
    try { url = await uploadFile(file, { maxPx: 512, quality: 0.85 }); }
    catch (err) { toast.error(apiError(err)); setPhotoBusy(false); return; }
    await savePhoto(url);
  };
  const logoInput = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const saveCompany = async (next = cForm, msg = "Údaje firmy uloženy.") => {
    if (!next.name.trim()) { toast.error("Název firmy nesmí být prázdný."); return false; }
    setCBusy(true);
    try { await api.patch("/company", next); setCSaved(next); toast.success(msg); refresh(); return true; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setCBusy(false); }
  };
  // Logo se uloží hned po nahrání (dřív „Logo nahráno — uložte firmu“, snadno se zapomnělo).
  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoBusy(true);
    try {
      const url = await uploadFile(file, { maxPx: 800, quality: 0.9 });
      const next = { ...cSaved, logo_url: url };
      setCForm((c) => ({ ...c, logo_url: url }));
      await saveCompany(next, "Logo uloženo.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLogoBusy(false); }
  };
  const removeLogo = async () => {
    setCForm((c) => ({ ...c, logo_url: "" }));
    await saveCompany({ ...cSaved, logo_url: "" }, "Logo odebráno.");
  };
  // 259: IČO → registr ARES doplní název, sídlo, DIČ a plátce DPH a rovnou to uloží.
  const loadAres = async () => {
    const ico = (cForm.ico || "").replace(/\D/g, "");
    if (ico.length !== 8) { toast.error("IČO má 8 číslic."); return; }
    setAres("busy");
    try {
      const { data } = await api.get(`/investors/ares/${ico}`);
      const next = { ...cForm, ico, name: data.name || cForm.name, dic: data.dic || "",
        street: data.street || cForm.street, zip: data.zip || cForm.zip, city: data.city || cForm.city };
      setCForm(next);
      setAres("done");
      await saveCompany(next, "Údaje firmy načteny z registru a uloženy.");
      const vat = typeof data.vat_payer === "boolean" ? data.vat_payer : !!data.dic;
      setAresVat(true);
      if (vat !== vatPayer) await saveVatPayer(vat, true);
    } catch (e) {
      setAres(e.response?.status === 404 ? "nf" : null);
      toast.error(apiError(e));
    }
  };
  const companyDirty = JSON.stringify(cForm) !== JSON.stringify(cSaved);

  const changeCurrency = async (code) => {
    if (code === currency || curBusy) return;
    setCurBusy(true);
    try {
      await api.patch("/company", { currency: code });
      setActiveCurrency(code);
      toast.success(`Měna firmy nastavena na ${code}.`);
      // Tvrdý reload: všechny částky napříč aplikací se přeformátují do nové měny.
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Změna měny selhala.");
      setCurBusy(false);
    }
  };

  // Viditelnost sekcí podle role (stejně jako dřív záložky).
  const KEYS = [
    ["profil", true], ["firma", admin], ["dochazka", admin], ["tym", admin || canMcp], ["doklady", admin],
    ["ukoly", admin || user?.role === "MANAGER"], ["predplatne", isOwner], ["pristupy", canMcp],
  ].filter(([, show]) => show).map(([k]) => k);
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const urlTab = ALIAS[rawTab] || rawTab;
  const tabInUrl = KEYS.includes(urlTab) ? urlTab : null;
  // Telefon: bez ?tab= seznam sekcí; počítač: bez ?tab= první záložka.
  const listMode = mobile && KEYS.length > 1 && !tabInUrl;
  const tab = tabInUrl || KEYS[0];
  const tabsUl = useSlidingPill(tab);   // 385: podtržení záložky dojede s pružinou
  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key) next.set("tab", key); else next.delete("tab");
    setParams(next, { replace: !mobile });          // na telefonu jde zpět systémovým gestem
    if (mobile) window.scrollTo({ top: 0 });
  };

  const statusOf = (k) => ({
    profil: "Fotka, upozornění, vzhled",
    firma: company?.ico ? (company?.name || "") : "Doplň IČO",
    dochazka: att ? `${MODE_LABELS[att.attendance_mode_default] || ""} · ${hoursText(att.daily_target_hours)}` : "",
    tym: !admin ? "Kdo co vidí" : autoNewProj ? "Celý tým na zakázkách" : "Tým přidáváš ručně",
    doklady: `${cats.length + rCats.length} kategorií`,
    ukoly: "Podle fáze",
    predplatne: company?.display_status?.label || "",
    pristupy: "AI, protokol",
  }[k]);

  // ---------------- obsah sekcí ----------------
  const firmaOk = !!company?.ico;
  const profil = (
    <div className="space-y-6">
      {/* 306: průvodce nastavením není dokončený — na telefonu sem vede avatar s tečkou */}
      <GuideRow />
      <Group>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3.5">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Avatar name={form.name || user?.name} photo={form.photo_url} size={56} className="bg-brand/15 text-brand" testid="profile-avatar" />
            <div className="min-w-0">
              <div className="font-bold text-[17px] truncate">{form.name || user?.name}</div>
              <div className="text-[13px] text-muted-foreground truncate">{user?.email}</div>
            </div>
          </div>
          <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={pickPhoto} data-testid="profile-photo-input" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => photoInput.current?.click()} disabled={photoBusy} className="h-10 flex-1 sm:flex-none font-bold" data-testid="profile-photo-btn">
              <Camera size={16} /> {photoBusy ? "Nahrávám…" : (form.photo_url ? "Změnit fotku" : "Nahrát fotku")}
            </Button>
            {form.photo_url && (
              <Button variant="ghost" onClick={() => savePhoto(null)} disabled={photoBusy} className="h-10 font-bold text-muted-foreground hover:text-destructive" data-testid="profile-photo-remove">Odebrat</Button>
            )}
          </div>
        </div>
      </Group>
      <div>
        <Group title="Kontakt">
          <Row label="Jméno" field><input data-testid="settings-name" aria-label="Jméno" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={rowInput} /></Row>
          <Row label="Telefon" field><input data-testid="settings-phone" aria-label="Telefon" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={rowInput} /></Row>
          {/* 293: vlastní e-mail jde změnit — kód přijde na novou adresu */}
          <Row label="E-mail" sub={<span className="break-all">{user?.email}</span>} testid="settings-email-row">
            <Button type="button" variant="outline" onClick={() => setEmailOpen(true)} className="h-9 px-4 font-bold shrink-0" data-testid="settings-email-change">Změnit</Button>
          </Row>
        </Group>
        <div className="flex md:justify-end mt-3">
          <Button onClick={saveProfile} data-testid="settings-save" className="h-11 w-full md:w-auto font-bold px-6">Uložit profil</Button>
        </div>
      </div>
      <Group title="Upozornění" info="Na displej telefonu chodí označení @ a komentáře u tvých úkolů a k tvé docházce. Zvuk, vibrace a Nerušit se řídí nastavením telefonu. Ve zvonečku v aplikaci je všechno vždy.">
        <PushRows />
        <Row label="E-mail, když mě někdo označí" sub="Upozornění v aplikaci chodí vždy.">
          <Switch checked={user?.notify_mention_email !== false} data-testid="mention-email-switch" className="shrink-0"
            onCheckedChange={async (v) => {
              try { await api.patch(`/team/${user.id}`, { notify_mention_email: v }); refresh(); toast.success(v ? "E-maily při označení zapnuty." : "E-maily při označení vypnuty."); }
              catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} />
        </Row>
      </Group>
      <Group title="Vzhled">
        <Row label="Motiv aplikace">
          <Segmented testid="settings-theme-toggle" value={theme === "dark" ? "dark" : "light"} onChange={() => toggle()}
            options={[{ value: "light", label: <span className="inline-flex items-center gap-1.5"><Sun size={14} /> Světlý</span> },
                      { value: "dark", label: <span className="inline-flex items-center gap-1.5"><Moon size={14} /> Tmavý</span> }]} />
        </Row>
        <Row label="Skleněné plochy" wrap testid="settings-glass-row"
          sub={glass.mode === "auto" ? (glass.on ? "Na starším počítači se samo vypne, když nestíhá." : "Vypnuto automaticky — počítač nestíhal.") : glass.mode === "on" ? "Vždy zapnuté." : "Plné plochy, nejrychlejší."}>
          <Segmented testid="settings-glass" value={glass.mode} onChange={(m) => setGlassMode(m)}
            options={[{ value: "auto", label: "Automaticky", testid: "settings-glass-auto" }, { value: "on", label: "Zapnuto", testid: "settings-glass-on" }, { value: "off", label: "Vypnuto", testid: "settings-glass-off" }]} />
        </Row>
        {/* 394: jak kdo chce vidět částky v Dokladech a Objednávkách — u účtu, na všech zařízeních; stránku jde přepnout v Zobrazení */}
        {MONEY_ROLES.includes(user?.role) && (
          <Row label="Částky v Dokladech a Objednávkách" wrap testid="settings-amounts-row"
            sub={user?.amounts_pref ? "Na všech tvých zařízeních. Stránku přepneš v Zobrazení." : `Podle firmy (${company?.vat_payer === false ? "neplátce DPH" : "plátce DPH"}). Stránku přepneš v Zobrazení.`}>
            <Segmented testid="settings-amounts" value={defaultAmountMode(company, user)} onChange={saveAmounts}
              options={[{ value: "net", label: "Bez DPH", testid: "settings-amounts-net" }, { value: "gross", label: "S DPH", testid: "settings-amounts-gross" },
                        { value: "both", label: "Obojí", testid: "settings-amounts-both" }]} />
          </Row>
        )}
      </Group>
      <EmailChangeDialog open={emailOpen} onOpenChange={setEmailOpen} currentEmail={user?.email} onDone={refresh} />
      <AppVersionLine />
    </div>
  );

  const firmaField = (label, key, extra = {}) => (
    <Row label={label} testid={`company-row-${key}`} field>
      <input data-testid={`company-${key}`} aria-label={label} value={cForm[key]} onChange={(e) => setCForm({ ...cForm, [key]: e.target.value })}
        className={rowInput} {...extra} />
    </Row>
  );
  const firma = (
    <div className="space-y-6">
      <Group title="Registr" info="Zadej IČO a z veřejného registru ARES doplníme název, sídlo, DIČ a jestli je firma plátce DPH. Nic nepřepisuješ ručně."
        footer={ares === "done" ? <span className="text-success font-semibold">Načteno z registru ARES a uloženo.</span>
          : ares === "nf" ? <span className="text-destructive">Firma s tímhle IČO v registru není — zkontroluj číslo.</span>
          : !firmaOk ? "Začni tady: zadej IČO a zbytek se doplní sám." : null}>
        <Row label="IČO" wrap field testid="company-row-ico">
          <span className="flex items-center gap-2 w-full sm:w-auto">
            <input data-testid="company-ico" aria-label="IČO" inputMode="numeric" maxLength={8} placeholder="8 číslic" value={cForm.ico}
              onChange={(e) => { setCForm({ ...cForm, ico: e.target.value.replace(/\D/g, "").slice(0, 8) }); if (ares === "nf") setAres(null); }}
              onKeyDown={(e) => e.key === "Enter" && loadAres()}
              className="h-10 min-w-0 flex-1 sm:flex-none sm:w-40 rounded-[10px] border-0 bg-secondary px-3 sm:px-2.5 text-left sm:text-right text-[16px] sm:text-[15px] tabular-nums focus:outline-none focus:ring-2 focus:ring-brand/40 placeholder:text-muted-foreground/60" />
            <Button onClick={loadAres} disabled={ares === "busy"} data-testid="company-ares" className="h-10 font-bold shrink-0">
              {ares === "busy" ? "Hledám…" : "Načíst z ARES"}
            </Button>
          </span>
        </Row>
      </Group>
      <div>
        <Group title="Údaje" info="Tisknou se v hlavičce stavebního deníku a na dokumentech.">
          {firmaField("Název", "name")}
          {firmaField("DIČ", "dic", { placeholder: "CZ12345678" })}
          {firmaField("Ulice a č. p.", "street")}
          {firmaField("PSČ", "zip", { inputMode: "numeric" })}
          {firmaField("Město", "city")}
          {firmaField("Web", "web", { placeholder: "https://…" })}
        </Group>
        {companyDirty && (
          <div className="flex md:justify-end mt-3">
            <Button onClick={() => saveCompany()} disabled={cBusy} data-testid="company-save" className="h-11 w-full md:w-auto font-bold px-6">
              {cBusy ? "Ukládám…" : "Uložit firmu"}
            </Button>
          </div>
        )}
      </div>
      <Group title="Daně, odvody a měna" info="Plátce DPH: ekonomika počítá náklady i výnosy bez DPH. Neplátce vidí částky s DPH — to je jeho skutečný náklad. Odvody: u zaměstnanců (HPP) je hodinová sazba hrubá mzda a firma k ní platí sociální (24,8 %) a zdravotní (9 %) pojištění a zákonné pojištění odpovědnosti (stavebnictví 0,98 %) — celkem 34,78 %. V jiném oboru je pojištění odpovědnosti jiné, procento si upravte. U dohody (DPP) jen v měsíci nad limitem, u lidí na IČO nikdy — typ se nastavuje u člověka v Týmu. Práce na zakázce se počítá i s odvody. Změna měny nepřepočítává staré částky, mění jen jejich zobrazení.">
        <div data-testid="vat-payer-row">
          <Row label="Plátce DPH" sub={aresVat ? "Podle registru ARES — přepni, jen když nesedí." : vatPayer ? "Ekonomika počítá bez DPH" : "Částky s DPH jsou váš náklad"}>
            <Switch checked={vatPayer} disabled={vatBusy} onCheckedChange={(v) => { setAresVat(false); saveVatPayer(v); }} data-testid="vat-payer-switch" className="shrink-0" />
          </Row>
        </div>
        <Row label="Odvody k mzdám" sub={laborPct.replace(",", ".") === "0" ? "Práce se počítá jen hrubou mzdou" : "Pojištění, které firma platí k hrubé mzdě zaměstnanců"} testid="labor-overhead-row" field>
          <span className="inline-flex items-center gap-1 shrink-0">
            <input value={laborPct} disabled={laborBusy} inputMode="decimal" maxLength={6} aria-label="Odvody k mzdám v procentech"
              onChange={(e) => setLaborPct(e.target.value)} onBlur={saveLaborPct}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              className="h-10 w-[72px] rounded-[10px] bg-secondary px-2.5 text-right text-base md:text-[15px] tabular-nums focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:opacity-60"
              data-testid="labor-overhead-input" />
            <span className="text-[15px] text-muted-foreground">%</span>
          </span>
        </Row>
        <div data-testid="currency-section">
          <Row label="Měna" sub={isOwner ? null : "Měnu může změnit jen majitel."}>
            <Segmented value={currency} onChange={changeCurrency} disabled={!isOwner || curBusy}
              options={CURRENCIES.map((c) => ({ value: c.code, label: `${c.symbol} ${c.code}`, testid: `currency-${c.code}` }))} />
          </Row>
        </div>
      </Group>
      <Group title="Logo" footer="Tiskne se v deníku a na certifikátech zaškolení.">
        <Row label={
          <span className="flex items-center gap-3">
            <span className="h-12 w-24 rounded-[10px] border border-dashed border-border bg-secondary/40 grid place-items-center overflow-hidden shrink-0" data-testid="company-logo-preview">
              {cForm.logo_url ? <SignedImg url={cForm.logo_url} alt="Logo firmy" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-muted-foreground font-normal">Bez loga</span>}
            </span>
          </span>}>
          <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={pickLogo} data-testid="company-logo-input" />
          <span className="flex gap-2 shrink-0">
            {cForm.logo_url && <Button variant="ghost" onClick={removeLogo} disabled={cBusy} className="h-10 font-bold text-muted-foreground hover:text-destructive">Odebrat</Button>}
            <Button variant="outline" onClick={() => logoInput.current?.click()} disabled={logoBusy} className="h-10 font-bold" data-testid="company-logo">
              <Camera size={16} /> {logoBusy ? "Nahrávám…" : (cForm.logo_url ? "Změnit" : "Nahrát")}
            </Button>
          </span>
        </Row>
      </Group>
      {isOwner && company?.is_test && (
        <Group title="Smazat data" info="Jen v testovacím účtu: smaže vybrané doklady, sklad, pokladnu nebo docházku. Lidé, zakázky a nastavení zůstanou.">
          <Row label="Smazat data firmy" sub="Vyberete, co smazat, a potvrdíte opsáním SMAZAT" testid="reset-data-row">
            <Button variant="outline" onClick={() => setResetOpen(true)} data-testid="reset-data-open"
              className="h-10 font-bold shrink-0 text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive">
              <Trash2 size={16} /> Smazat…
            </Button>
          </Row>
        </Group>
      )}
      <ResetDataDialog open={resetOpen} onClose={() => setResetOpen(false)} />
    </div>
  );

  const dochazka = !att ? <p className="text-sm text-muted-foreground" data-testid="attendance-loading">Načítám nastavení docházky…</p> : (
    <div className="space-y-6" data-testid="attendance-settings">
      <Group title="Jak lidé zapisují práci" footer="Jednotlivým lidem jde režim změnit v jejich kartě.">
        <div className="grid grid-cols-2 gap-2 p-3">
          {[["site", "Na stavbě", "Přihlásí se v telefonu při příchodu a odchodu."], ["daysheet", "Denní výkaz", "Večer rozepíše hodiny na zakázky."]].map(([v, t, d]) => (
            <button key={v} type="button" onClick={() => changeMode(v, t)} disabled={attBusy} data-testid={`att-mode-${v}`} aria-pressed={att.attendance_mode_default === v}
              className={`text-left p-3 rounded-[12px] transition-colors ${att.attendance_mode_default === v ? "bg-brand/10 shadow-[inset_0_0_0_1.5px_hsl(var(--accent-orange))]" : "bg-secondary hover:bg-secondary/70"}`}>
              <div className="font-bold text-sm">{t}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{d}</div>
            </button>
          ))}
        </div>
        {myOverride && (
          <div className="px-4 py-3 bg-amber-500/10" data-testid="att-my-override">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-bold">Máte vlastní režim: {MODE_LABELS[myOverride]}</div>
                <p className="text-muted-foreground mt-0.5">Váš osobní režim přebíjí firemní nastavení. Ostatních pracovníků se to netýká.</p>
                {isOwner && <Button variant="outline" onClick={clearMyOverride} disabled={attBusy} data-testid="att-clear-override" className="h-9 mt-2 w-full sm:w-auto font-bold">Používat firemní režim</Button>}
              </div>
            </div>
          </div>
        )}
      </Group>
      <Group title="Pracovní den" info={`Přestávka se odečte sama ze zakázky, na které člověk v tu dobu byl — nikdo ji nezadává. Směna kratší než ${att.break_min_hours ?? 6} h se nekrátí.`}>
        <Row label="Délka směny" sub="podle ní poznáme přesčas">
          <span className="inline-flex items-center rounded-full bg-secondary p-0.5 shrink-0" data-testid="att-target">
            <button type="button" aria-label="Kratší směna" disabled={attBusy || att.daily_target_hours <= 1} onClick={() => saveAtt({ daily_target_hours: Math.max(1, att.daily_target_hours - 0.5) })}
              className="w-9 h-9 rounded-full grid place-items-center hover:bg-card disabled:opacity-40"><Minus size={16} /></button>
            <b className="min-w-[56px] text-center tabular-nums text-[15px]">{hoursText(att.daily_target_hours)}</b>
            <button type="button" aria-label="Delší směna" disabled={attBusy || att.daily_target_hours >= 24} onClick={() => saveAtt({ daily_target_hours: Math.min(24, att.daily_target_hours + 0.5) })}
              className="w-9 h-9 rounded-full grid place-items-center hover:bg-card disabled:opacity-40"><Plus size={16} /></button>
          </span>
        </Row>
        {/* 402: jak se v šichtovnici a souhrnu pro mzdy počítá přesčas */}
        <Row label="Přesčas počítat" wrap testid="att-overtime-row"
          sub={att.overtime_mode === "monthly" ? "Kolik kdo odpracoval nad fond za celý měsíc — kratší dny se vyrovnají." : "Každý den zvlášť nad délku směny, víkendy a svátky celé — podle zákoníku práce."}>
          <Segmented testid="att-overtime" value={att.overtime_mode || "daily"} onChange={(v) => saveAtt({ overtime_mode: v })} disabled={attBusy}
            options={[{ value: "daily", label: "Každý den", testid: "att-overtime-daily" }, { value: "monthly", label: "Za měsíc", testid: "att-overtime-monthly" }]} />
        </Row>
        <Row label="Přestávka">
          <Select value={String(att.break_auto_minutes ?? 30)} onValueChange={(v) => saveAtt({ break_auto_minutes: Number(v) })}>
            <SelectTrigger className="h-10 w-[150px] shrink-0" data-testid="att-break-minutes"><SelectValue /></SelectTrigger>
            <SelectContent>{[0, 15, 30, 45, 60, 90, 120].map((m) => <SelectItem key={m} value={String(m)}>{m === 0 ? "Bez přestávky" : `${m} min`}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        {(att.break_auto_minutes ?? 30) > 0 && (
          <Row label="Přestávka začíná">
            <TimeSelect value={att.break_start || "12:00"} from={600} to={840} testid="att-break-start" disabled={attBusy} onChange={(v) => saveAtt({ break_start: v })} />
          </Row>
        )}
      </Group>
      <Group title="Když zapomenou odejít" info="Kdo se do té hodiny neodhlásí, systém ho odhlásí sám. Další den si v telefonu doplní skutečný konec, co dělal a fotky — vedoucí to schválí. Dokud to nedoplní, do práce se nepřihlásí (podle nastavení níž). Výjimku pro jednoho člověka — třeba stavbyvedoucího, který si docházku vede jen pro sebe — dáš v Tým › člověk › Nastavení.">
        <div data-testid="att-auto-close-at-row">
          <Row label="Automatické odhlášení v">
            <TimeSelect value={att.attendance_auto_close_at || "21:00"} from={1020} to={1410} testid="att-auto-close-at" disabled={attBusy} onChange={(v) => saveAtt({ attendance_auto_close_at: v })} />
          </Row>
        </div>
        <div data-testid="att-block-row">
        <Row label="Nedoplněný den" sub="neodeslaný výkaz nebo nedoplněný konec směny" wrap>
          <Select value={String(att.unfinished_days_block ?? 1)} onValueChange={(v) => saveAtt({ unfinished_days_block: Number(v) })}>
            <SelectTrigger className="h-10 w-full sm:w-[210px] shrink-0" data-testid="att-block"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[...new Set([0, 1, 2, 3, att.unfinished_days_block ?? 1])].sort((a, b) => a - b).map((n) => (
                <SelectItem key={n} value={String(n)}>{n === 0 ? "Pustit do práce" : n === 1 ? "Nepustit (doporučeno)" : `Nepustit po ${n} dnech`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Row>
        </div>
      </Group>
      <Group title="Příchod a odchod" footer="Platí pro dělníky a subdodavatele. Stavbyvedoucí, majitel a asistentka povinnost nemají.">
        <div data-testid="att-gps-required">
          <Row label="Poloha při příchodu" sub={att.gps_required ? "Povinná — bez polohy se nepřihlásí." : "Nepovinná — uvidíš štítek bez GPS."}>
            <Switch checked={!!att.gps_required} disabled={attBusy} onCheckedChange={(v) => saveAtt({ gps_required: v })} data-testid="att-gps-switch" className="shrink-0" />
          </Row>
        </div>
        <div data-testid="att-checkout-photos">
          <Row label="Fotka z práce při odchodu" sub={att.checkout_photos_required ? "Povinná — aspoň 1 fotka. Nemusí být ze stavby." : "Nepovinná — přidat může, nemusí."}>
            <Switch checked={!!att.checkout_photos_required} disabled={attBusy} onCheckedChange={(v) => saveAtt({ checkout_photos_required: v })} data-testid="att-photos-switch" className="shrink-0" />
          </Row>
        </div>
      </Group>
      {/* 403: příplatky do podkladu pro mzdy s částkami — mění jen kdo vidí sazby (majitel, asistentka) */}
      {admin && att.wage_premiums && (
        <Group title="Příplatky ke mzdě" testid="att-premiums"
          info="Používá je podklad pro mzdy s částkami (Docházka › Šichtovnice, Ekonomika). Výchozí jsou zákonná minima. Počítají se ze sazby člověka — zákon je počítá z průměrného výdělku, který aplikace nezná, proto jsou částky orientační."
          footer={PREMIUM_ROWS.some(([k]) => att.wage_premiums[k] !== att.wage_premium_defaults?.[k]) && (
            <button type="button" onClick={() => saveAtt({ wage_premiums: att.wage_premium_defaults })} disabled={attBusy}
              className="font-bold text-brand hover:underline" data-testid="att-premiums-reset">Obnovit podle zákona</button>)}>
          {PREMIUM_ROWS.map(([k, label, law]) => (
            <Row key={k} label={label} sub={`zákon: nejméně ${law} %`} testid={`att-premium-${k}`}>
              <PercentField value={att.wage_premiums[k]} disabled={attBusy} testid={`att-premium-${k}-input`}
                onSave={(v) => saveAtt({ wage_premiums: { ...att.wage_premiums, [k]: v } })} />
            </Row>
          ))}
        </Group>
      )}
    </div>
  );

  const tym = (
    <div className="space-y-6">
      {admin && (
        <Group title="Nové zakázky a lidé" testid="team-settings" footer="Šetří ruční přidávání lidí do každé stavby.">
          <Row label="Nová zakázka dostane celý tým" sub="Dělníci, subdodavatelé a stavbyvedoucí. V nové zakázce to jde odškrtnout.">
            <Switch checked={autoNewProj} disabled={teamFlagBusy} onCheckedChange={(v) => saveTeamFlag("auto_team_new_projects", v)} data-testid="auto-team-new-projects" className="shrink-0" />
          </Row>
          <Row label="Nový člověk na rozběhnuté zakázky" sub="Po pozvání se přidá rovnou do všech rozpracovaných zakázek.">
            <Switch checked={autoNewMem} disabled={teamFlagBusy} onCheckedChange={(v) => saveTeamFlag("auto_team_new_members", v)} data-testid="auto-team-new-members" className="shrink-0" />
          </Row>
        </Group>
      )}
      {canMcp && <div className="min-w-0"><PermissionsMatrix /></div>}
    </div>
  );

  const doklady = (
    <div className="space-y-6" data-testid="categories">
      <Group title="Náklady (doklady)" info="Do nich se řadí faktury a doklady v ekonomice zakázek. V tomhle pořadí se nabízejí u dokladu. Přejmenování přepíše i staré doklady.">
        <div className="p-4"><TagEditor items={cats} kind="cost" onAdd={(t) => saveCats([...cats, t])} onRemove={(c) => saveCats(cats.filter((x) => x !== c))} onReorder={saveCats}
          onRenamed={(list) => { setCats(list); refresh(); }} placeholder="Nová kategorie" testid="cat" /></div>
      </Group>
      <Group title="Výdaje (účtenky)" info="Pro účtenky a paragony — PHM, kancelář, cestovné, stravné…">
        <div className="p-4"><TagEditor items={rCats} kind="receipt" onAdd={(t) => saveRCats([...rCats, t])} onRemove={(c) => saveRCats(rCats.filter((x) => x !== c))} onReorder={saveRCats}
          onRenamed={(list) => { setRCats(list); refresh(); }} placeholder="Nová kategorie" testid="rcat" /></div>
      </Group>
    </div>
  );

  const pristupy = (
    <div className="space-y-6">
      <div className="min-w-0"><McpConnector /></div>
      {isOwner && (
        <Section icon={ShieldCheck} title="Bezpečnostní protokol" desc="Přehled citlivých akcí ve vaší firmě (posledních 500 záznamů)." testid="audit-log-section">
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="audit-empty">Zatím žádné záznamy.</p>
          ) : (
            <div className="divide-y divide-border max-h-96 overflow-auto">
              {audit.map((a) => (
                <div key={a.id} className="py-2.5 flex items-start justify-between gap-3 text-sm" data-testid="audit-row">
                  <div className="min-w-0">
                    <span className="font-semibold">{AUDIT_LABELS[a.action] || a.action}</span>
                    {a.user_name && <span className="text-muted-foreground"> · {a.user_name}</span>}
                    {a.detail && <div className="text-xs text-muted-foreground mt-0.5">{a.detail}</div>}
                  </div>
                  <div className="text-right shrink-0 text-xs text-muted-foreground">
                    <div>{formatDateTime(a.created_at)}</div>
                    {a.ip && <div className="opacity-70">{a.ip}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );

  const CONTENT = {
    profil, firma, dochazka, tym, doklady,
    ukoly: <div className="min-w-0"><WorkflowUkoly canEdit={admin} /></div>,
    predplatne: <SubscriptionPanel Section={Section} />,
    pristupy,
  };
  const wide = tab === "ukoly" || tab === "predplatne" || tab === "pristupy" || tab === "tym";

  // ---------------- telefon: seznam sekcí ----------------
  // 259: stejný vzhled jako Menu (šedé dlaždice ikon, nadpisy skupin, oblé karty). Můj profil tu
  // není — otevírá se kartou se jménem nahoře v Menu (jinak by tam byl dvakrát).
  if (listMode) {
    const groups = [["Firma", ["firma", "doklady"]], ["Lidé a práce", ["dochazka", "tym", "ukoly"]], ["Účet", ["predplatne", "pristupy"]]]
      .map(([head, g]) => [head, g.filter((k) => KEYS.includes(k))]).filter(([, g]) => g.length);
    return (
      <div className="space-y-1" data-testid="settings-page">
        <PageHeader title="Nastavení" />
        {groups.map(([head, g]) => (
          <div key={head}>
            <div className="text-[12px] font-bold tracking-[.08em] uppercase text-muted-foreground px-1.5 pt-4 pb-[7px]">{head}</div>
            {g.map((k, i) => {
              const m = SECTION_META[k];
              return (
                <button key={k} type="button" onClick={() => setTab(k)} data-testid={`settings-tab-${k}`}
                  className={`relative flex items-center gap-[13px] w-full h-[54px] px-3.5 bg-card text-left text-[16px] font-semibold active:bg-secondary ${i === 0 ? "rounded-t-[20px]" : ""} ${i === g.length - 1 ? "rounded-b-[20px]" : ""}`}>
                  {i > 0 && <span aria-hidden="true" className="absolute left-[61px] right-0 top-0 h-px bg-border" />}
                  <span aria-hidden="true" className="w-[34px] h-[34px] rounded-[10px] grid place-items-center shrink-0 bg-secondary text-muted-foreground">
                    <m.icon size={19} />
                  </span>
                  <span className="flex-1 min-w-0 truncate">{m.label}</span>
                  {k === "firma" && !firmaOk && <span className="w-2 h-2 rounded-full bg-destructive shrink-0" aria-label="Chybí údaje" />}
                  <span className="text-[14px] font-normal text-muted-foreground truncate max-w-[42%]">{statusOf(k)}</span>
                  <ChevronRight size={16} className="text-muted-foreground/60 shrink-0" aria-hidden="true" />
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="settings-page">
      {mobile && KEYS.length > 1 ? (
        <div>
          <button type="button" onClick={() => setTab(null)} data-testid="settings-back"
            className="-ml-2 h-10 px-2 inline-flex items-center gap-0.5 pp-ink font-semibold text-[16px]">
            <ChevronLeft size={22} aria-hidden="true" /> Nastavení
          </button>
          <h1 className="font-heading text-[28px] font-extrabold tracking-tight mt-1">{SECTION_META[tab].label}</h1>
        </div>
      ) : <PageHeader title="Nastavení" />}

      {/* 231: podtržené záložky jako v Ekonomice — na počítači a tabletu zůstávají (259: nové roztřídění). */}
      {!mobile && KEYS.length > 1 && (
        <div className="border-b border-border -mt-1">
          <div ref={tabsUl.ref} role="tablist" aria-label="Sekce nastavení" className="relative flex gap-1 overflow-x-auto no-scrollbar">
            {tabsUl.pill("bg-brand", { underline: true })}
            {KEYS.map((k) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`} data-pill-on={tab === k || undefined}
                className={`flex items-center gap-2 px-4 min-h-[44px] text-sm font-bold whitespace-nowrap shrink-0 border-b-2 -mb-px transition-colors ${
                  tab === k ? `${tabsUl.ready ? "border-transparent" : "border-brand"} text-brand` : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {SECTION_META[k].short || SECTION_META[k].label}
                {k === "firma" && !firmaOk && <span className="w-2 h-2 rounded-full bg-destructive" aria-label="Chybí údaje" />}
                {k === "predplatne" && company?.display_status?.label && (
                  <span className="rounded-full px-2 py-px text-[10.5px] font-bold bg-secondary text-muted-foreground">{company.display_status.label}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div role="tabpanel" className={wide ? "min-w-0" : "max-w-[760px] min-w-0"}>{CONTENT[tab]}</div>
    </div>
  );
}
